import { API, DOMAIN } from '@autopass-web/domain';
import HttpClient from '../http-client';

class UserHttpClient extends HttpClient {
  constructor(props: any) {
    super(props);
    this.apiConfig = DOMAIN.API_URL[API.USER];
    this.httpClient = this.createHttpClient({
      headers: this.apiConfig.headers,
      baseUrl: this.apiConfig.baseUrl,
    });

    this.httpClient.interceptors.request.use((value: any) =>
      this.interceptRequest(value),
    );
    this.httpClient.interceptors.response.use(
      (value: any) => this.interceptResponseResult(value),
      (error: any) => this.interceptResponseError(error, this.httpClient),
    );
  }

  fetchPois = (
    props = {
      types: 'Parkinglot,GasStation',
      limit: '',
      boundingBox: '',
      collectionIds: [],
      ignoreIds: '',
      parkinglotCooperationStates: 'all',
      gasStationCooperationStates: 'in_cooperated,coming_soon',
    },
    config = {},
  ) => {
    return this.post(
      `pois`,
      {
        types: props?.types,
        limit: props?.limit,
        bounding_box: props?.boundingBox,
        collection_ids: props?.collectionIds,
        ignore_ids: props?.ignoreIds,
        gas_station_cooperation_states: props.gasStationCooperationStates,
        parkinglot_cooperation_states: props.parkinglotCooperationStates,
        version: '10.1.0',
      },
      config,
    );
  };

  fetchBanksCreditCardCategories = () =>
    this.get(`/banks/credit_card_categories`);

  fetchBankCreditCardCategory = (id: string) =>
    this.get(`/banks/${id}/credit_card_categories`);

  fetchCreditCardBenefits = (id: string) =>
    this.get(`/credit_card_categories/${id}/benefits`);

  // 場站 API

  fetchPoi = (id: string) => this.get(`/pois/${id}`);

  fetchPoiCollections = () => this.get(`/poi_collections`);

  fetchGasStations = ({
    page = 1,
    collectionIds,
  }: {
    page: number;
    collectionIds: string[];
  }) =>
    this.get('/gas_stations/list', {
      page,
      collection_ids: collectionIds,
      version: '10.1.0',
    });

  fetchGasStationCell = (id: string) =>
    this.get(`/gas_stations/parking_cells/${id}`);

  fetchParkinglots = ({
    boundingBox,
    collectionIds,
  }: {
    boundingBox: string;
    collectionIds: string[];
  }) =>
    this.get(`/parkinglots`, {
      bounding_box: boundingBox,
      collection_ids: collectionIds,
    });

  fetchParkinglotFromCode = ({
    parkinglotSerialNumber,
    serialNumber,
  }: {
    parkinglotSerialNumber: string;
    serialNumber: string;
  }) =>
    this.get(`parking_codes/${serialNumber}`, {
      serial_number: parkinglotSerialNumber,
    });

  fetchParkingRecordBills = ({
    parkinglotId,
    vehicleType,
    plateNumber,
  }: {
    parkinglotId: string;
    vehicleType: string;
    plateNumber: string;
  }) =>
    this.get('parking_records/query_parking', {
      parkinglot_id: parkinglotId,
      plate_number_fuzzy: plateNumber,
      vehicle_type: vehicleType,
    });

  createParkingTicket = ({
    parkinglotId,
    parkingRecordId,
    vehicleType,
    plateNumber,
    kioskId,
  }: {
    parkinglotId: string;
    parkingRecordId: string;
    vehicleType: string;
    plateNumber: string;
    kioskId: string;
  }) =>
    this.post(`parking_tickets/create`, {
      parkinglot_id: parkinglotId,
      parking_record_id: parkingRecordId,
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      parking_record_kiosk_id: kioskId || undefined,
    });

  fetchChainStoresList = (page = 1) =>
    this.get('chain_stores/list', {
      page,
      cooperation_states: 'in_cooperated',
    });

  fetchChainStores = ({
    boundingBox,
    collectionIds,
  }: {
    boundingBox: string;
    collectionIds: string[];
  }) =>
    this.get(`/chain_stores`, {
      bounding_box: boundingBox,
      collection_ids: collectionIds,
      cooperation_states: 'in_cooperated',
    });

  fetchChainStore = (id: string) => this.get(`/chain_stores/${id}`);

  // USER

  fetchOtp = (mobile: number) =>
    this.post(`/otp`, {
      mobile,
      need_check_email: false,
    });

  fetchOneTimeToken = (token: string) =>
    this.post(`/one_time_token`, {
      one_time_token: token,
    });

  mobileLogin = ({ mobile, code }: { mobile: number; code: string }) =>
    this.post(`/auth`, {
      mobile,
      code,
      with_email: false,
    });

  usernameLogin = ({
    username,
    password,
    subAccount,
  }: {
    username: string;
    password: string;
    subAccount: string;
  }) =>
    this.post('/login', {
      username,
      password,
      sub_account_username: subAccount,
    });

  register = ({
    email,
    plateNumber,
    vehicleType,
  }: {
    email: string;
    plateNumber: string;
    vehicleType: string;
  }) =>
    this.post(`/web_auth`, {
      email,
      plate_number: plateNumber,
      vehicle_type: vehicleType,
    });

  logout = () => this.post(`/logout`);

  fetchUser = () => this.get(`/me`);

  updateUserInfo = ({
    email,
    displayName,
    receipt,
    enableOnStreetParkingAutoPay,
    agreeTermsOfTaipeiCityParkinglotService,
  }: {
    email: string;
    displayName: string;
    receipt: {
      barcode: string;
      ubn: string;
      citizen_digital_number: string;
      love_code: string;
    };
    enableOnStreetParkingAutoPay: boolean;
    agreeTermsOfTaipeiCityParkinglotService: boolean;
  }) => {
    return this.post(`/me`, {
      email: email,
      display_name: displayName,
      enable_on_street_parking_auto_pay: enableOnStreetParkingAutoPay,
      receipt,
      agree_terms_of_taipei_city_parkinglot_service:
        agreeTermsOfTaipeiCityParkinglotService,
    });
  };

  updatePassword = ({
    oldPassword,
    newPassword,
  }: {
    oldPassword: string;
    newPassword: string;
  }) =>
    this.post(`/me/password`, {
      password: oldPassword,
      new_password: newPassword,
    });

  updateOnStreetParkingAutopay = (enableOnStreetParkingAutoPay: boolean) =>
    this.post(`/me`, {
      enable_on_street_parking_auto_pay: enableOnStreetParkingAutoPay,
    });

  fetchRefuelingRequestsLatest = () => this.get(`/refueling_requests/latest`);

  answerRefuelingRequests = ({ id, answer }: { id: string; answer: string }) =>
    this.post(`/refueling_requests/${id}`, {
      answer,
    });

  getChainStorePaymentInfo = (id: string) => this.get(`/invoices/${id}`);

  boards = ({
    codes,
    partnerId,
    distributor,
  }: {
    codes: string;
    partnerId?: string;
    distributor?: string;
  }) =>
    this.get('/bulletin_boards', {
      codes,
      partnerId,
      distributor,
    });

  bootstraps = () => {
    return this.get('/bootstraps');
  };

  fetchStreetParkingBills = ({
    city,
    plateNumber,
  }: {
    city: string;
    plateNumber: string;
  }) =>
    this.get('/on_street_parking_bills', {
      city,
      plate_number: plateNumber,
    });

  payStreetParkingBills = ({
    city,
    codes,
    plateNumber,
  }: {
    city: string;
    codes: [];
    plateNumber: string;
  }) => {
    return this.post('on_street_parking_bills/pay', {
      city,
      bill_codes: codes,
      plate_number: plateNumber,
    });
  };

  fetchLicensePlates = () => this.get('/license_plates');

  addLicensePlate = ({
    plateNumber,
    vehicleType,
    energySource,
  }: {
    plateNumber: string;
    vehicleType?: string;
    energySource?: string;
  }) =>
    this.post('/license_plates', {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      energy_source: energySource,
    });

  deleteLicensePlate = (id: string) => this.delete(`/license_plates/${id}`);

  bindLicensePlate = (id: string) =>
    this.post(`/license_plates/${id}/bind_city_parkinglot`);

  modifyLicensePlate = ({
    id,
    plateNumber,
    vehicleType,
  }: {
    id: string;
    plateNumber: string;
    vehicleType: string;
  }) =>
    this.put(`/license_plates/${id}`, {
      plate_number: plateNumber,
      vehicle_type: vehicleType,
    });

  initMochipayValidation = () => this.post(`/wallets/init_mochipay_validation`);

  bindCpcPay = (cpcId: string) =>
    this.post(`/wallets/bind_cpc_pay`, {
      cpc_id: cpcId,
    });

  fetchNpcCards = () => {
    return this.get(`/wallets/get_npc_valid_card_types`);
  };

  bindNpcPay = ({
    cardType,
    enableRedeem,
  }: {
    cardType: string;
    enableRedeem: string;
  }) => {
    return this.post(`/wallets/bind_npc_pay`, {
      card_type: cardType,
      enable_credit_point_redeem: enableRedeem,
    });
  };

  enableRedeemDiscount = () => this.post(`/wallets/redeem_bonus_points/enable`);

  disableRedeemDiscount = () =>
    this.post(`/wallets/redeem_bonus_points/disable`);

  bindMochipayWallet = ({
    token,
    transactionId,
  }: {
    token: string;
    transactionId: string;
  }) =>
    this.post(`/wallets/mochipay_wallet`, {
      token: token,
      transaction_id: transactionId,
      default_distributor: true,
    });

  deleteCreditCard = () => this.delete(`/wallets/credit_cards`);

  fetchDebitAccountTransactions = ({
    id,
    startAt,
    endAt,
    page,
    totalPage,
  }: {
    id: string;
    startAt: string;
    endAt: string;
    page: number;
    totalPage: number;
  }) =>
    this.get(`/debit_accounts/${id}/transactions`, {
      start_at: startAt,
      end_at: endAt,
      page: page,
      per_page: totalPage,
    });

  sendDebitCardReport = ({
    id,
    startAt,
    endAt,
  }: {
    id: string;
    startAt: string;
    endAt: string;
  }) =>
    this.post(`/debit_accounts/${id}/transactions/export`, {
      start_at: startAt,
      end_at: endAt,
    });

  fetchTwoFactorAuth = () => this.get(`/two_factor_auth`);

  enableTwoFactorAuth = (totp: string) =>
    this.post(`/two_factor_auth/enable`, {
      totp_code: totp,
    });

  disableTwoFactorAuth = () => this.post(`/two_factor_auth/disable`);

  fetchOrders = ({
    page = 1,
    pageSize = 100,
    transactionTypes,
    plateNumber,
    startAt,
    endAt,
  }: {
    page: number;
    pageSize: number;
    transactionTypes: string;
    plateNumber: string;
    startAt: string;
    endAt: string;
  }) =>
    this.get(`/orders/simple`, {
      transaction_types: transactionTypes,
      plate_number: plateNumber,
      start_at: startAt,
      end_at: endAt,
      page: page,
      per_page: pageSize,
    });

  fetchOrderDetail = (url: string) => this.get(url);

  fetchMochiCoins = ({ state, page = 1 }: { state: string; page: number }) =>
    this.get(`/mochi_coin_transactions`, {
      state: state,
      page: page,
      limit: 20,
    });

  fetchParkingRecord = () => this.get(`/parking_records`);

  parkingRecordCancel = (parkingRecordId: string) =>
    this.post(`/parking_records/${parkingRecordId}/cancel`);

  parkingRecordLeave = (parkingRecordId: string) =>
    this.post(`/parking_records/${parkingRecordId}/leave`);

  updatePushToken = (token: string) =>
    this.post('/distributor_push_token', {
      push_token: token,
    });

  fetchPromotions = ({
    transactionType,
    distributor,
    filter,
    parkinglotOnline,
  }: {
    transactionType: string;
    distributor: string;
    filter: string;
    parkinglotOnline: boolean;
  }) =>
    this.get('/promotions', {
      transaction_type: transactionType,
      filter,
      description_with_type: false,
      distributor,
      parkinglot_online: parkinglotOnline,
    });

  fetchPromotionCollections = (distributor: string) =>
    this.get('/promotion_collections', {
      distributor,
    });

  fetchPromotionUserUsageStatus = (id: string) =>
    this.get(`/promotions/${id}/all_user_usage_status`);

  refreshToken = (token: string) =>
    this.post('/oauth/token', {
      refresh_token: token,
      grant_type: 'refresh_token',
    });

  fetchAppleMapToken = () => this.get('/apple_map_token');

  fetchPaymentToken = (plateNumber?: string) =>
    this.post('/payment_tokens', { plate_number: plateNumber });

  fetchPaymentResult = (token: string) => this.post(`/payment_tokens/${token}`);

  fetchContractlessLicensePlates = (page: number) => {
    return this.get(`/mochipass/contractless_license_plates`, {
      page,
      per_page: 20,
    });
  };

  applyMochiPassContract = ({
    id,
    oilType,
  }: {
    id: string;
    oilType: string;
  }) => {
    return this.post(`/mochipass/contracts`, {
      license_plate_id: id,
      oil_type: oilType,
    });
  };

  fetchMochiPassContract = (page: number) => {
    return this.get(`/mochipass/contracts`, {
      page,
      per_page: 20,
    });
  };

  fetchCouponList = ({
    accessToken,
    state,
  }: {
    accessToken: string;
    state: 'all_redeemed' | 'available' | 'expired';
  }) => {
    return this.get('/promotion_certificates', {
      access_token: accessToken,
      state,
    });
  };

  exchangeCoupon = ({
    accessToken,
    code,
    campaignId,
    plateNumber,
    userInfo,
  }: {
    accessToken?: string;
    code: string;
    campaignId?: string;
    plateNumber?: string;
    userInfo?: any;
  }) => {
    return this.post('/redemption_campaigns/redeem', {
      access_token: accessToken,
      redemption_code: code,
      redemption_campaign_id: campaignId,
      plate_number: plateNumber,
      personal_info: userInfo,
    });
  };

  fetchCouponInfo = (id: string) => {
    return this.get(`/redemption_campaigns/${id}`);
  };

  fetchActiveLicensePlates = () => this.get('/me/active_license_plates');

  fetchOrdersSummary = () => this.get('/me/paid_orders_exist_collection');

  downloadUserReports = ({
    transactionTypes,
    plateNumber,
    startAt,
    endAt,
    ubn,
  }: {
    transactionTypes: string[];
    plateNumber: string;
    startAt: string;
    endAt: string;
    ubn: string;
  }) =>
    this.get('/user_reports', {
      transaction_types: transactionTypes,
      plate_number: plateNumber,
      start_at: startAt,
      end_at: endAt,
      ubn,
    });

  // enterprise driver api
  enterpriseValidateMobile = (mobile: number) => {
    return this.post('/enterprise_drivers/validate_mobile', { mobile });
  };

  enterpriseLogin = ({ mobile, code }: { mobile: number; code: string }) => {
    return this.post(`/enterprise_drivers/auth`, {
      mobile,
      code,
    });
  };

  fetchEnterprisePaymentState = (id: string) => {
    return this.get(`/enterprise_drivers/${id}/enterprise_payment_status`);
  };

  fetchEnterpriseLicensePlates = (id: string) => {
    return this.get(`/enterprise_drivers/${id}/license_plates`);
  };

  enterpriseLogout = (id: string) => {
    return this.post(`/enterprise_drivers/${id}/logout`);
  };
}

export default UserHttpClient;
